import { Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { Suspense, lazy } from 'react'

/**
 * Forever-alias: old /plans-of-care/* and /poc/sign/* URLs redirect to /forms/*.
 * Old links (notifications, bookmarks, emails) stay valid. Do not remove.
 */
function LegacyFormsRedirect() {
  const location = useLocation()
  const target =
    location.pathname
      .replace(/^\/plans-of-care/, '/forms')
      .replace(/^\/poc\/sign/, '/forms/sign') + location.search
  return <Navigate to={target} replace />
}

// Layouts
import PublicLayout from './components/layout/PublicLayout'
import AppLayout from './components/layout/AppLayout'
import ProtectedRoute from './components/shared/ProtectedRoute'
import SubscriptionGuard from './components/shared/SubscriptionGuard'
import RoleGuard from './components/shared/RoleGuard'
import ScrollToTop from './components/shared/ScrollToTop'
import FeatureFlag from './components/shared/FeatureFlag'

// PWA
import { InstallPrompt } from './components/pwa/InstallPrompt'

// Loading spinner
import { Spinner } from './components/ui'

// Public pages (eagerly loaded for fast initial paint)
import Landing from './routes/public/Landing'
import Login from './routes/public/Login'
import Signup from './routes/public/Signup'
import ForgotPassword from './routes/public/ForgotPassword'
import About from './routes/public/About'
import Contact from './routes/public/Contact'
import Privacy from './routes/public/Privacy'
import Terms from './routes/public/Terms'
import Help from './routes/public/Help'
import WhatsNew from './routes/public/WhatsNew'
import Documentation from './routes/public/Documentation'
import Status from './routes/public/Status'
import AcceptInvitation from './routes/public/AcceptInvitation'
import SignPOC from './routes/public/SignPOC'

// App pages (lazy loaded)
const Dashboard = lazy(() => import('./routes/app/Dashboard'))
const ClientList = lazy(() => import('./routes/app/clients/ClientList'))
const ClientDetail = lazy(() => import('./routes/app/clients/ClientDetail'))
const ClientCreate = lazy(() => import('./routes/app/clients/ClientCreate'))
const AuthorizationList = lazy(() => import('./routes/app/authorizations/AuthorizationList'))
const AuthorizationCreate = lazy(() => import('./routes/app/authorizations/AuthorizationCreate'))
const Schedule = lazy(() => import('./routes/app/schedule/Schedule'))
const ScheduleHistory = lazy(() => import('./routes/app/schedule/ScheduleHistory'))
const VisitDetail = lazy(() => import('./routes/app/schedule/VisitDetail'))
const VisitCreate = lazy(() => import('./routes/app/schedule/VisitCreate'))
const OpenShifts = lazy(() => import('./routes/app/open-shifts/OpenShifts'))
const PlanOfCareList = lazy(() => import('./routes/app/plans-of-care/PlanOfCareList'))
const PlanOfCareDetail = lazy(() => import('./routes/app/plans-of-care/PlanOfCareDetail'))
const PlanOfCareCreate = lazy(() => import('./routes/app/plans-of-care/PlanOfCareCreate'))
const PlanOfCareEdit = lazy(() => import('./routes/app/plans-of-care/PlanOfCareEdit'))
const POCAdminSign = lazy(() => import('./routes/app/plans-of-care/POCAdminSign'))
// Assessments reuse the Plan of Care infrastructure (templates, signing, PDF fill).
// The same components handle both form categories — see ?category=assessment.
const AssessmentNewRedirect = lazy(() => import('./routes/app/assessments/AssessmentNewRedirect'))
const POCTemplateList = lazy(() => import('./routes/app/poc-templates/POCTemplateList'))
const POCTemplateSetup = lazy(() => import('./routes/app/poc-templates/POCTemplateSetup'))
const IncidentList = lazy(() => import('./routes/app/incidents/IncidentList'))
const IncidentDetail = lazy(() => import('./routes/app/incidents/IncidentDetail'))
const IncidentCreate = lazy(() => import('./routes/app/incidents/IncidentCreate'))
const CaregiverDashboard = lazy(() => import('./routes/app/caregiver/CaregiverDashboard'))
const CaregiverSchedule = lazy(() => import('./routes/app/caregiver/CaregiverSchedule'))
const CaregiverSettings = lazy(() => import('./routes/app/caregiver/CaregiverSettings'))
const AvailabilitySettings = lazy(() => import('./routes/app/caregiver/AvailabilitySettings'))
const VisitNoteCreate = lazy(() => import('./routes/app/caregiver/VisitNoteCreate'))
const MessageList = lazy(() => import('./routes/app/messages/MessageList'))
const MessageThreadView = lazy(() => import('./routes/app/messages/MessageThread'))
const NewMessage = lazy(() => import('./routes/app/messages/NewMessage'))
const NotificationList = lazy(() => import('./routes/app/notifications/NotificationList'))
const Settings = lazy(() => import('./routes/app/settings/Settings'))
const FamilyFeedbackManagement = lazy(() => import('./routes/app/settings/components/FamilyFeedbackManagement'))
const FormTemplates = lazy(() => import('./routes/app/form-templates/FormTemplates'))
const CaregiverList = lazy(() => import('./routes/app/caregivers/CaregiverList'))
const CaregiverDetail = lazy(() => import('./routes/app/caregivers/CaregiverDetail'))
const CaregiverCreate = lazy(() => import('./routes/app/caregivers/CaregiverCreate'))
const ClientEdit = lazy(() => import('./routes/app/clients/ClientEdit'))
const VisitEdit = lazy(() => import('./routes/app/schedule/VisitEdit'))
const CaregiverEdit = lazy(() => import('./routes/app/caregivers/CaregiverEdit'))
const IncidentEdit = lazy(() => import('./routes/app/incidents/IncidentEdit'))
const ImportData = lazy(() => import('./routes/app/import/ImportData'))
// Compliance is handled by AsiliGuide (optional integration)
const EVVDashboard = lazy(() => import('./routes/app/evv/EVVDashboard'))
const EVVExceptions = lazy(() => import('./routes/app/evv/EVVExceptions'))
const ClaimsDashboard = lazy(() => import('./routes/app/billing/ClaimsDashboard'))
const ClaimDetail = lazy(() => import('./routes/app/billing/ClaimDetail'))
const CreateClaim = lazy(() => import('./routes/app/billing/CreateClaim'))
const BulkClaimCreate = lazy(() => import('./routes/app/billing/BulkClaimCreate'))
const ERADashboard = lazy(() => import('./routes/app/billing/ERADashboard'))
const ERADetail = lazy(() => import('./routes/app/billing/ERADetail'))
const PayerEnrollments = lazy(() => import('./routes/app/billing/PayerEnrollments'))
const AppealsList = lazy(() => import('./routes/app/billing/AppealsList'))
const EligibilityDashboard = lazy(() => import('./routes/app/eligibility/EligibilityDashboard'))
const FamilyPortal = lazy(() => import('./routes/app/family/FamilyPortal'))
const FamilyRegister = lazy(() =>
  import('./routes/app/family/FamilyPortal').then((module) => ({ default: module.FamilyRegister }))
)
const Records = lazy(() => import('./routes/app/records/Records'))
const TimeOffPage = lazy(() => import('./routes/app/settings/TimeOffPage'))

// Admin pages
const SystemTemplateList = lazy(() => import('./routes/app/admin/SystemTemplateList'))
const SystemTemplateEditor = lazy(() => import('./routes/app/admin/SystemTemplateEditor'))

// Loading fallback
function PageLoader() {
  return (
    <div className="flex items-center justify-center min-h-[400px]">
      <Spinner size="lg" />
    </div>
  )
}

export default function App() {
  return (
    <>
      <ScrollToTop />
      <InstallPrompt />
      <Routes>
      {/* Auth pages without header/footer */}
      <Route path="/login" element={<Login />} />
      <Route path="/signup" element={<Signup />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/accept-invitation" element={<AcceptInvitation />} />
      <Route path="/sign-poc" element={<SignPOC />} />
      <Route
        path="/family/register"
        element={
          <Suspense fallback={<PageLoader />}>
            <FamilyRegister />
          </Suspense>
        }
      />

      {/* Public routes with header/footer */}
      <Route element={<PublicLayout />}>
        <Route path="/" element={<Landing />} />
      </Route>

      {/* Public pages without PublicLayout (they have their own header/footer) */}
      <Route path="/about" element={<About />} />
      <Route path="/contact" element={<Contact />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/help" element={<Help />} />
      <Route path="/whats-new" element={<WhatsNew />} />
      <Route path="/docs" element={<Documentation />} />
      <Route path="/status" element={<Status />} />

      {/* Protected app routes */}
      <Route
        element={
          <ProtectedRoute>
            <SubscriptionGuard>
              <AppLayout />
            </SubscriptionGuard>
          </ProtectedRoute>
        }
      >
        {/* Dashboard - Admin, Supervisor, Billing, QA (caregivers use /my-dashboard) */}
        <Route
          path="/dashboard"
          element={
            <RoleGuard allowedPermissions={['dashboard.read']}>
              <Suspense fallback={<PageLoader />}>
                <Dashboard />
              </Suspense>
            </RoleGuard>
          }
        />

        {/* Clients - Admin, Supervisor, Billing, QA */}
        <Route
          path="/clients"
          element={
            <RoleGuard allowedPermissions={['clients.read']}>
              <Suspense fallback={<PageLoader />}>
                <ClientList />
              </Suspense>
            </RoleGuard>
          }
        />
        <Route
          path="/clients/new"
          element={
            <RoleGuard allowedPermissions={['clients.manage']}>
              <Suspense fallback={<PageLoader />}>
                <ClientCreate />
              </Suspense>
            </RoleGuard>
          }
        />
        <Route
          path="/clients/:id"
          element={
            <RoleGuard allowedPermissions={['clients.read']}>
              <Suspense fallback={<PageLoader />}>
                <ClientDetail />
              </Suspense>
            </RoleGuard>
          }
        />
        <Route
          path="/clients/:id/edit"
          element={
            <RoleGuard allowedPermissions={['clients.manage']}>
              <Suspense fallback={<PageLoader />}>
                <ClientEdit />
              </Suspense>
            </RoleGuard>
          }
        />

        {/* Caregivers - Admin, Supervisor only */}
        <Route
          path="/caregivers"
          element={
            <RoleGuard allowedPermissions={['caregivers.read']}>
              <Suspense fallback={<PageLoader />}>
                <CaregiverList />
              </Suspense>
            </RoleGuard>
          }
        />
        <Route
          path="/caregivers/new"
          element={
            <RoleGuard allowedPermissions={['caregivers.manage']}>
              <Suspense fallback={<PageLoader />}>
                <CaregiverCreate />
              </Suspense>
            </RoleGuard>
          }
        />
        <Route
          path="/caregivers/:id"
          element={
            <RoleGuard allowedPermissions={['caregivers.read']}>
              <Suspense fallback={<PageLoader />}>
                <CaregiverDetail />
              </Suspense>
            </RoleGuard>
          }
        />
        <Route
          path="/caregivers/:id/edit"
          element={
            <RoleGuard allowedPermissions={['caregivers.manage']}>
              <Suspense fallback={<PageLoader />}>
                <CaregiverEdit />
              </Suspense>
            </RoleGuard>
          }
        />

        {/* Schedule - Admin, Supervisor (caregivers use /my-schedule) */}
        <Route
          path="/schedule"
          element={
            <RoleGuard allowedPermissions={['schedule.read']}>
              <Suspense fallback={<PageLoader />}>
                <Schedule />
              </Suspense>
            </RoleGuard>
          }
        />
        <Route
          path="/schedule/history"
          element={
            <RoleGuard allowedPermissions={['schedule.read']}>
              <Suspense fallback={<PageLoader />}>
                <ScheduleHistory />
              </Suspense>
            </RoleGuard>
          }
        />
        <Route
          path="/schedule/new"
          element={
            <RoleGuard allowedPermissions={['schedule.manage']}>
              <Suspense fallback={<PageLoader />}>
                <VisitCreate />
              </Suspense>
            </RoleGuard>
          }
        />
        {/* Visit Detail - any authenticated staff role; caregivers see only
            their own visits via in-page filtering. No `allowedPermissions` so
            role check applies (permissions would otherwise short-circuit it). */}
        <Route
          path="/schedule/:id"
          element={
            <RoleGuard allowedRoles={['admin', 'supervisor', 'billing_specialist', 'qa_officer', 'caregiver']}>
              <Suspense fallback={<PageLoader />}>
                <VisitDetail />
              </Suspense>
            </RoleGuard>
          }
        />
        <Route
          path="/schedule/:id/edit"
          element={
            <RoleGuard allowedPermissions={['schedule.manage']}>
              <Suspense fallback={<PageLoader />}>
                <VisitEdit />
              </Suspense>
            </RoleGuard>
          }
        />

        {/* Records - Admin, Supervisor, QA (Visit Notes + Incidents tabs) */}
        <Route
          path="/records"
          element={
            <RoleGuard allowedPermissions={['records.read']}>
              <Suspense fallback={<PageLoader />}>
                <Records />
              </Suspense>
            </RoleGuard>
          }
        />
        {/* Time Off Management */}
        <Route
          path="/time-off"
          element={
            <RoleGuard allowedPermissions={['time_off.manage']}>
              <Suspense fallback={<PageLoader />}>
                <TimeOffPage />
              </Suspense>
            </RoleGuard>
          }
        />
        {/* Incidents — separate page, separate `incidents.read` permission */}
        <Route
          path="/incidents"
          element={
            <RoleGuard allowedPermissions={['incidents.read']}>
              <Suspense fallback={<PageLoader />}>
                <IncidentList />
              </Suspense>
            </RoleGuard>
          }
        />
        {/* Incident Create - Admin, Supervisor, Caregiver */}
        <Route
          path="/incidents/new"
          element={
            <RoleGuard allowedPermissions={['incidents.create']}>
              <Suspense fallback={<PageLoader />}>
                <IncidentCreate />
              </Suspense>
            </RoleGuard>
          }
        />
        <Route
          path="/incidents/:id"
          element={
            <RoleGuard allowedPermissions={['incidents.read']}>
              <Suspense fallback={<PageLoader />}>
                <IncidentDetail />
              </Suspense>
            </RoleGuard>
          }
        />
        <Route
          path="/incidents/:id/edit"
          element={
            <RoleGuard allowedPermissions={['incidents.review']}>
              <Suspense fallback={<PageLoader />}>
                <IncidentEdit />
              </Suspense>
            </RoleGuard>
          }
        />

        {/* Redirect /documentation to /records */}
        <Route path="/documentation" element={<Navigate to="/records" replace />} />

        {/* Authorizations - Admin, Supervisor, Billing */}
        <Route
          path="/authorizations"
          element={
            <RoleGuard allowedPermissions={['authorizations.read']}>
              <Suspense fallback={<PageLoader />}>
                <AuthorizationList />
              </Suspense>
            </RoleGuard>
          }
        />
        <Route
          path="/authorizations/new"
          element={
            <RoleGuard allowedPermissions={['authorizations.manage']}>
              <Suspense fallback={<PageLoader />}>
                <AuthorizationCreate />
              </Suspense>
            </RoleGuard>
          }
        />
        {/* Redirect authorization detail to list (inline edit available there) */}
        <Route
          path="/authorizations/:id"
          element={<Navigate to="/authorizations" replace />}
        />
        <Route
          path="/authorizations/:id/edit"
          element={<Navigate to="/authorizations" replace />}
        />

        {/* Forms (canonical) — holds Plans of Care, Assessments, and state forms (CMS-485, DMAS-97, etc.) */}
        <Route
          path="/forms"
          element={
            <RoleGuard allowedPermissions={['forms.read']}>
              <Suspense fallback={<PageLoader />}>
                <PlanOfCareList />
              </Suspense>
            </RoleGuard>
          }
        />
        <Route
          path="/forms/new"
          element={
            <RoleGuard allowedPermissions={['forms.manage']}>
              <Suspense fallback={<PageLoader />}>
                <PlanOfCareCreate />
              </Suspense>
            </RoleGuard>
          }
        />
        <Route
          path="/forms/:id"
          element={
            <RoleGuard allowedPermissions={['forms.read']}>
              <Suspense fallback={<PageLoader />}>
                <PlanOfCareDetail />
              </Suspense>
            </RoleGuard>
          }
        />
        <Route
          path="/forms/:id/edit"
          element={
            <RoleGuard allowedPermissions={['forms.manage']}>
              <Suspense fallback={<PageLoader />}>
                <PlanOfCareEdit />
              </Suspense>
            </RoleGuard>
          }
        />
        <Route
          path="/forms/sign/:requestId"
          element={
            <RoleGuard allowedPermissions={['forms.send']}>
              <Suspense fallback={<PageLoader />}>
                <POCAdminSign />
              </Suspense>
            </RoleGuard>
          }
        />

        {/*
         * Forever-aliases: old /plans-of-care/* and /poc/sign/* URLs redirect to /forms/*.
         * Do NOT remove — notifications, emails, and bookmarks with the old paths still exist in the wild.
         */}
        <Route path="/plans-of-care/*" element={<LegacyFormsRedirect />} />
        <Route path="/poc/sign/:requestId" element={<LegacyFormsRedirect />} />

        {/*
         * Assessments — share the Plan of Care infrastructure entirely.
         *
         *   /assessments/new          → PlanOfCareCreate with ?category=assessment
         *   /assessments/:id          → PlanOfCareDetail (formCategory tells the page it's an assessment)
         *
         * AssessmentNewRedirect just appends ?category=assessment to whatever
         * query string is present and forwards to /forms/new.
         */}
        <Route
          path="/assessments/new"
          element={
            <RoleGuard allowedPermissions={['forms.manage']}>
              <Suspense fallback={<PageLoader />}>
                <AssessmentNewRedirect />
              </Suspense>
            </RoleGuard>
          }
        />
        <Route
          path="/assessments/:id"
          element={
            <RoleGuard allowedPermissions={['forms.read']}>
              <Suspense fallback={<PageLoader />}>
                <PlanOfCareDetail />
              </Suspense>
            </RoleGuard>
          }
        />
        {/* Legacy: editing an old custom-editor assessment redirects to its detail page */}
        <Route
          path="/assessments/:id/edit"
          element={
            <RoleGuard allowedPermissions={['forms.manage']}>
              <Suspense fallback={<PageLoader />}>
                <PlanOfCareDetail />
              </Suspense>
            </RoleGuard>
          }
        />

        {/* POC Templates - Admin only */}
        <Route
          path="/poc-templates"
          element={
            <RoleGuard allowedRoles={['admin']}>
              <Suspense fallback={<PageLoader />}>
                <POCTemplateList />
              </Suspense>
            </RoleGuard>
          }
        />
        <Route
          path="/poc-templates/new"
          element={
            <RoleGuard allowedRoles={['admin']}>
              <Suspense fallback={<PageLoader />}>
                <POCTemplateSetup />
              </Suspense>
            </RoleGuard>
          }
        />
        <Route
          path="/poc-templates/:id"
          element={
            <RoleGuard allowedRoles={['admin']}>
              <Suspense fallback={<PageLoader />}>
                <POCTemplateSetup />
              </Suspense>
            </RoleGuard>
          }
        />

        {/* System Templates - Admin only (super admin for managing state templates) */}
        <Route
          path="/admin/system-templates"
          element={
            <RoleGuard allowedRoles={['admin']}>
              <Suspense fallback={<PageLoader />}>
                <SystemTemplateList />
              </Suspense>
            </RoleGuard>
          }
        />
        <Route
          path="/admin/system-templates/new"
          element={
            <RoleGuard allowedRoles={['admin']}>
              <Suspense fallback={<PageLoader />}>
                <SystemTemplateEditor />
              </Suspense>
            </RoleGuard>
          }
        />
        <Route
          path="/admin/system-templates/:id"
          element={
            <RoleGuard allowedRoles={['admin']}>
              <Suspense fallback={<PageLoader />}>
                <SystemTemplateEditor />
              </Suspense>
            </RoleGuard>
          }
        />

        {/* Messages - gated by messages.read permission (granted to every
            staff role + caregivers by default in resolvePermissions). */}
        <Route
          path="/messages"
          element={
            <RoleGuard allowedPermissions={['messages.read']}>
              <Suspense fallback={<PageLoader />}>
                <MessageList />
              </Suspense>
            </RoleGuard>
          }
        />
        <Route
          path="/messages/new"
          element={
            <RoleGuard allowedPermissions={['messages.read']}>
              <Suspense fallback={<PageLoader />}>
                <NewMessage />
              </Suspense>
            </RoleGuard>
          }
        />
        <Route
          path="/messages/:threadId"
          element={
            <RoleGuard allowedPermissions={['messages.read']}>
              <Suspense fallback={<PageLoader />}>
                <MessageThreadView />
              </Suspense>
            </RoleGuard>
          }
        />

        {/* Reports - Redirect to Settings (Reports tab is in Settings) */}
        <Route path="/reports" element={<Navigate to="/settings" replace />} />

        {/* Settings - Admin, Supervisor only */}
        <Route
          path="/settings"
          element={
            <RoleGuard allowedPermissions={['settings.manage']}>
              <Suspense fallback={<PageLoader />}>
                <Settings />
              </Suspense>
            </RoleGuard>
          }
        />
        <Route
          path="/family-feedback"
          element={
            <RoleGuard allowedPermissions={['family_feedback.manage']}>
              <Suspense fallback={<PageLoader />}>
                <FamilyFeedbackManagement />
              </Suspense>
            </RoleGuard>
          }
        />

        {/* Form Templates - Admin only */}
        <Route
          path="/form-templates"
          element={
            <RoleGuard allowedPermissions={['forms.manage']}>
              <Suspense fallback={<PageLoader />}>
                <FormTemplates />
              </Suspense>
            </RoleGuard>
          }
        />

        {/* Open Shifts - Admin, Supervisor, Caregiver */}
        <Route
          path="/open-shifts"
          element={
            <RoleGuard allowedPermissions={['open_shifts.read']}>
              <Suspense fallback={<PageLoader />}>
                <OpenShifts />
              </Suspense>
            </RoleGuard>
          }
        />

        {/* Notifications - All roles */}
        <Route
          path="/notifications"
          element={
            <RoleGuard allowedPermissions={['notifications.read']}>
              <Suspense fallback={<PageLoader />}>
                <NotificationList />
              </Suspense>
            </RoleGuard>
          }
        />

        {/* Audit Logs - Redirect to Settings (Audit Logs tab is in Settings) */}
        <Route path="/audit-logs" element={<Navigate to="/settings" replace />} />

        {/* Import Data - Admin, Supervisor only */}
        <Route
          path="/import"
          element={
            <RoleGuard allowedPermissions={['settings.manage']}>
              <Suspense fallback={<PageLoader />}>
                <ImportData />
              </Suspense>
            </RoleGuard>
          }
        />

        {/* Compliance is handled by AsiliGuide (optional integration) */}

        {/* EVV Dashboard - Admin, Supervisor, Billing Specialist, QA Officer */}
        <Route
          path="/evv"
          element={
            <FeatureFlag flag="evv">
              <RoleGuard allowedPermissions={['evv.read']}>
                <Suspense fallback={<PageLoader />}>
                  <EVVDashboard />
                </Suspense>
              </RoleGuard>
            </FeatureFlag>
          }
        />
        <Route
          path="/evv/exceptions"
          element={
            <FeatureFlag flag="evv">
              <RoleGuard allowedPermissions={['evv_exceptions.read']}>
                <Suspense fallback={<PageLoader />}>
                  <EVVExceptions />
                </Suspense>
              </RoleGuard>
            </FeatureFlag>
          }
        />

        {/* Billing & Claims - Admin, Supervisor, Billing Specialist */}
        <Route
          path="/billing"
          element={
            <FeatureFlag flag="billing">
              <RoleGuard allowedPermissions={['billing.read']}>
                <Suspense fallback={<PageLoader />}>
                  <ClaimsDashboard />
                </Suspense>
              </RoleGuard>
            </FeatureFlag>
          }
        />
        <Route
          path="/eligibility"
          element={
            <FeatureFlag flag="billing" featureName="Eligibility Checks">
              <RoleGuard allowedPermissions={['billing.read']}>
                <Suspense fallback={<PageLoader />}>
                  <EligibilityDashboard />
                </Suspense>
              </RoleGuard>
            </FeatureFlag>
          }
        />
        <Route
          path="/billing/claims/new"
          element={
            <FeatureFlag flag="billing">
              <RoleGuard allowedPermissions={['billing.manage']}>
                <Suspense fallback={<PageLoader />}>
                  <CreateClaim />
                </Suspense>
              </RoleGuard>
            </FeatureFlag>
          }
        />
        <Route
          path="/billing/claims/bulk"
          element={
            <FeatureFlag flag="billing">
              <RoleGuard allowedPermissions={['billing.manage']}>
                <Suspense fallback={<PageLoader />}>
                  <BulkClaimCreate />
                </Suspense>
              </RoleGuard>
            </FeatureFlag>
          }
        />
        <Route
          path="/billing/claims/:id"
          element={
            <FeatureFlag flag="billing">
              <RoleGuard allowedPermissions={['billing.read']}>
                <Suspense fallback={<PageLoader />}>
                  <ClaimDetail />
                </Suspense>
              </RoleGuard>
            </FeatureFlag>
          }
        />
        <Route
          path="/billing/era"
          element={
            <FeatureFlag flag="billing" featureName="Remittances">
              <RoleGuard allowedPermissions={['billing.read']}>
                <Suspense fallback={<PageLoader />}>
                  <ERADashboard />
                </Suspense>
              </RoleGuard>
            </FeatureFlag>
          }
        />
        <Route
          path="/billing/era/:id"
          element={
            <FeatureFlag flag="billing" featureName="Remittances">
              <RoleGuard allowedPermissions={['billing.read']}>
                <Suspense fallback={<PageLoader />}>
                  <ERADetail />
                </Suspense>
              </RoleGuard>
            </FeatureFlag>
          }
        />
        <Route
          path="/billing/enrollments"
          element={
            <FeatureFlag flag="billing" featureName="Payer Enrollments">
              <RoleGuard allowedPermissions={['billing.read']}>
                <Suspense fallback={<PageLoader />}>
                  <PayerEnrollments />
                </Suspense>
              </RoleGuard>
            </FeatureFlag>
          }
        />
        <Route
          path="/billing/appeals"
          element={
            <FeatureFlag flag="billing" featureName="Appeals">
              <RoleGuard allowedPermissions={['billing.read']}>
                <Suspense fallback={<PageLoader />}>
                  <AppealsList />
                </Suspense>
              </RoleGuard>
            </FeatureFlag>
          }
        />

        {/* Caregiver Routes - Caregiver only */}
        <Route
          path="/my-dashboard"
          element={
            <RoleGuard allowedRoles={['caregiver']}>
              <Suspense fallback={<PageLoader />}>
                <CaregiverDashboard />
              </Suspense>
            </RoleGuard>
          }
        />
        <Route
          path="/my-schedule"
          element={
            <RoleGuard allowedRoles={['caregiver']}>
              <Suspense fallback={<PageLoader />}>
                <CaregiverSchedule />
              </Suspense>
            </RoleGuard>
          }
        />
        <Route
          path="/request-time-off"
          element={
            <RoleGuard allowedRoles={['caregiver']}>
              <Suspense fallback={<PageLoader />}>
                <AvailabilitySettings
                  timeOffOnly
                  pageTitle="Request Time Off"
                  pageDescription="Submit and track your time off requests"
                  backHref="/my-dashboard"
                />
              </Suspense>
            </RoleGuard>
          }
        />
        <Route
          path="/visit/:visitId/complete"
          element={
            <RoleGuard allowedRoles={['caregiver']}>
              <Suspense fallback={<PageLoader />}>
                <VisitNoteCreate />
              </Suspense>
            </RoleGuard>
          }
        />
        {/* Also support /schedule/:id/complete for consistency */}
        <Route
          path="/schedule/:visitId/complete"
          element={
            <RoleGuard allowedRoles={['caregiver']}>
              <Suspense fallback={<PageLoader />}>
                <VisitNoteCreate />
              </Suspense>
            </RoleGuard>
          }
        />
        {/* Profile - any authenticated staff or caregiver. profile.read is in
            every role's defaults, so this guard is effectively "any signed-in user". */}
        <Route
          path="/profile"
          element={
            <RoleGuard allowedPermissions={['profile.read']}>
              <Suspense fallback={<PageLoader />}>
                <CaregiverSettings />
              </Suspense>
            </RoleGuard>
          }
        />
        {/* Legacy routes redirect to /profile for backward compatibility */}
        <Route path="/profile/edit" element={<Navigate to="/profile" replace />} />
        <Route path="/profile/notifications" element={<Navigate to="/profile" replace />} />
        <Route path="/profile/security" element={<Navigate to="/profile" replace />} />
        <Route path="/profile/availability" element={<Navigate to="/profile" replace />} />

        {/* Redirect /app to /dashboard */}
        <Route path="/app" element={<Navigate to="/dashboard" replace />} />
      </Route>

      {/* Family Portal - Protected route for family members */}
      <Route
        path="/family/*"
        element={
          <ProtectedRoute allowedRoles={['family', 'admin', 'supervisor']}>
            <Suspense fallback={<PageLoader />}>
              <FamilyPortal />
            </Suspense>
          </ProtectedRoute>
        }
      />

      {/* 404 */}
      <Route
        path="*"
        element={
          <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-950">
            <div className="text-center">
              <h1 className="text-6xl font-bold text-gray-200 dark:text-gray-800">404</h1>
              <p className="text-gray-600 dark:text-gray-300 mt-4">Page not found</p>
              <a href="/dashboard" className="text-primary-600 hover:underline mt-2 inline-block dark:text-primary-400">
                Go to Dashboard
              </a>
            </div>
          </div>
        }
      />
    </Routes>
    </>
  )
}
